/* RESPONSIBLE TEAM: team-standalone */
// eslint-disable-next-line no-restricted-imports
import { getFinStandaloneService } from 'embercom/lib/container-lookup';
import EditorConfig, {
  type PathConfigGenerationParams,
  type StepConfigGenerationParams,
} from 'embercom/objects/visual-builder/configuration/editor';
import { NewHubspotEmailConversationPathConfig } from 'embercom/objects/visual-builder/configuration/path';
import type Step from 'embercom/models/operator/visual-builder/step';
import { StandaloneChatMessageConfiguration } from 'embercom/objects/visual-builder/configuration/step/chat-message';
import { StandaloneTicketFinStepConfig } from 'embercom/objects/visual-builder/configuration/step/fin';
import { StandaloneAttributeCollectorConfiguration } from 'embercom/objects/visual-builder/configuration/step/attribute-collector';
import { stepTypes } from 'embercom/objects/visual-builder/configuration-list';
import HandoffToExternalTicketConfiguration from 'embercom/objects/visual-builder/configuration/step/handoff-to-external-ticket';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import HandoffToExternalAgentConfiguration from '../step/handoff-to-external-agent';

export default class NewHubspotEmailConversationBotConfig extends EditorConfig {
  private finStandaloneService: FinStandaloneService;

  constructor({
    areStepPaywallsActive,
    workflow,
  }: {
    areStepPaywallsActive: boolean;
    workflow?: Workflow;
  }) {
    super({ areStepPaywallsActive, workflow });
    this.finStandaloneService = getFinStandaloneService();
  }

  generatePathConfig({ path, editorState }: PathConfigGenerationParams) {
    return new NewHubspotEmailConversationPathConfig({
      path,
      editorState,
      supportsLocalAttributes: this.supportsLocalAttributes,
    });
  }

  generateStepConfig<S extends Step>({ step, pathConfig }: StepConfigGenerationParams<S>) {
    let stepConfigArgs = { step, pathConfig };

    switch (step.typeKey) {
      case stepTypes.attributeCollector:
        return new StandaloneAttributeCollectorConfiguration(stepConfigArgs);
      case stepTypes.chatMessage:
        return new StandaloneChatMessageConfiguration({
          ...stepConfigArgs,
          attributeService: this.attributeService,
        });
      case stepTypes.fin:
        return new StandaloneTicketFinStepConfig(stepConfigArgs);
      case stepTypes.handoffToExternalAgent: {
        let targetChannels =
          pathConfig.editorState.contentEditorService.activeObject?.targetChannels || [];

        let integration = this.finStandaloneService.standaloneIntegrations.find((integration) =>
          targetChannels.some((channel: string) =>
            integration.supportsExternalAgentHandoverFromChannels?.includes(channel),
          ),
        );
        return new HandoffToExternalAgentConfiguration({
          ...stepConfigArgs,
          integrationId: integration?.integrationId ?? undefined,
        });
      }
      case stepTypes.handoffToExternalTicket: {
        let targetChannels =
          pathConfig.editorState.contentEditorService.activeObject?.targetChannels || [];

        let integration = this.finStandaloneService.standaloneIntegrations.find((integration) =>
          targetChannels.some((channel: string) =>
            integration.supportsExternalTicketHandoverFromChannels?.includes(channel),
          ),
        );

        return new HandoffToExternalTicketConfiguration({
          ...stepConfigArgs,
          integrationId: integration?.integrationId ?? undefined,
        });
      }
      default:
        return super.generateStepConfig(stepConfigArgs);
    }
  }

  get supportsLocalAttributes(): boolean {
    return true;
  }

  get supportsConversationChannelTargeting(): boolean {
    return false;
  }

  get supportsSideSheet(): boolean {
    return false;
  }
}
